import {
  ref,
  reactive,
  onBeforeMount,
  computed,
  watch,
} from "@vue/composition-api";
import router from "@/router";
import store from "@/store";
import useModal from "@/@core/components/base-modal/hooks/useModal";
import useValidationModal from "@/@core/components/base-modal/hooks/useValidationModal";
import useInventoryManageCustom from "./useInventoryManageCustom";
import inventoryPrivilegeFormModel from "@/models/inventory/inventory-privilege-form";
import inventoryListModel from "@/models/inventory/inventory-list";
import usePagination from "@/views/components/pagination/usePagination";
import useFilterHeaderTable from "@/views/components/filter-header-table/useFilterHeaderTable";
import useDate from "@/utils/hooks/useDate";
import { PRIVILEGE_TYPE } from "@/configs/privilege/privilege";

export default function useInventoryManageMaster() {
  const { resolveDateWithSeperator } = useDate();
  const {
    resolveStatusLabel,
    resolveStatusClass,
    resolveStockModeClass,
    resolveStockModeLabel,
    resolveExpiredAlertLabel,
    resolveExpiredAlertClass
  } = useInventoryManageCustom();
  const {
    fetchEntryMasterList,
    fetchReorderList: fetchReorderPrivilegeList,
  } = inventoryListModel();
  const { fetchAssignEntryInventory } = inventoryPrivilegeFormModel();
  const { openModal, closeModal } = useModal();
  const { openDefaultErrorModal, openErrorModal } = useValidationModal();
  const { pagination, onPageChange, onFilterEntity } = usePagination();
  const {
    filters,
    ordering,
    onSetValueSearch,
    onSelectFilter,
  } = useFilterHeaderTable();
  const keyChange = ref(0);
  const currentIndex = ref(null);
  const currentIdSelect = ref(null);
  const privilegeSelect = ref(null);
  const tableModeReorder = ref(false);
  const loading = reactive({
    tableData: false,
    tableOverlay: false,
  });

  const fields = [
    { key: "seq", label: "No." },
    { key: "expired_alert", label: "Expired Status" },
    { key: "privilege.alt_reference", label: "Reference" },
    { key: "stock_mode", label: "Stock Mode" },
    { key: "image_url", label: "Image" },
    { key: "nameTh", label: "Name" },
    { key: "privilege_type", label: "Reward Type" },
    { key: "remaining_qty", label: "Master Balance" },
    { key: "total_qty", label: "Master Total" },
    { key: "master_remaining_qty", label: "Balance" },
    { key: "master_total_qty", label: "Total" },
    { key: "privilege.total_inventory_entry", label: "Total Assign Inv." },
    { key: "updated_by_name", label: "Updated By" },
    { key: "updatedtime", label: "Updated" },
    { key: "status", label: "Status" },
    { key: "actions", label: "" },
  ];

  const formStatus = reactive({
    validFrom: null,
    validThrough: null,
    status: null,
    expiredStatus: false
  });

  const inventoryMaster = ref(null);

  const inventoryManageActionList = reactive({
    settingManage: {
      label: "Manage Stock",
      value: 1,
      icon: "IconSettingManage",
      display: true,
    },
    assignInventory: {
      label: "Assign Inventory",
      value: 2,
      icon: "AssignBannerGroupIcon",
      display: true,
    },
    history: { label: "History", value: 3, icon: "IconHistory", display: true },
    reOrder: {
      label: "Reorder",
      value: 4,
      icon: "ReorderIcon",
      display: false,
    },
  });



  const resolveMappingTableAction = (type, item) => {
    switch (type) {
      case 1:
        openManageStockModal(item.item.privilege);
        return;
      case 2:
        privilegeSelect.value = item.item.privilege;
        modalType.assignInventory = true;
        openModal();
        return;
      case 3:
        router
          .push(
            "/inventory/privilege/history/group/" +
            item.item.privilege.privilege_id
          )
          .catch(() => { });
        return;
      case 4:
        modalType.reorder = true;
        openReorderModal(item.item);
        return;
      default:
        return;
    }
  };

  const statusOption = ref([
    { label: "Active", value: "1" },
    { label: "Inactive", value: "2" },
  ]);

  const inventoryManageTable = reactive({
    payload: [],
  });

  const rewardSelect = ref({});

  const modalType = reactive({
    reorder: false,
    assignInventory: false,
    manageStockReward: false,
    manageStockCouponExternal: false,
  });

  const onCloseModalInventoryManage = () => {
    closeModal();
    modalType.reorder = false;
    modalType.assignInventory = false;
    modalType.manageStockReward = false;
    modalType.manageStockCouponExternal = false;
  };

  const onAddPrivilegeToInventory = () => {
    closeModal();
    fetchPrivilegeInventory(false);
    setTimeout(() => {
      modalType.manageStockReward = false;
    }, 300);
  };

  const onClickReorder = () => {
    inventoryManageActionList.reOrder.display = !inventoryManageActionList
      .reOrder.display;
    tableModeReorder.value = !tableModeReorder.value;
    sortableRowOptions.value.disabled = !sortableRowOptions.value.disabled;
  };

  const handleRowChange = (event) => {
    fetchReorderList(
      inventoryManageTable.payload[event.oldDraggableIndex].privilege
        .privilege_id,
      inventoryManageTable.payload[event.oldDraggableIndex].seq,
      inventoryManageTable.payload[event.newDraggableIndex].seq
    );
    inventoryManageTable.payload.splice(
      event.newDraggableIndex,
      0,
      inventoryManageTable.payload.splice(event.oldDraggableIndex, 1)[0]
    );
    keyChange.value++;
  };

  const fetchReorderList = (id, currentPosition, newPosition) => {
    loading.tableOverlay = true;
    fetchReorderPrivilegeList.payload.Privilege_Id = id;
    fetchReorderPrivilegeList.payload.Inventory_Id = inventoryMaster.value;
    fetchReorderPrivilegeList.payload.From = currentPosition;
    fetchReorderPrivilegeList.payload.To = newPosition;
    store
      .dispatch("api/apiRequest", fetchReorderPrivilegeList)
      .then((response) => {
        fetchPrivilegeInventory(false);
      })
      .catch((error) => {
        openDefaultErrorModal(error);
        loading.tableOverlay = false;
      });
  };

  const sortableRowOptions = ref({
    chosenClass: "is-selected",
    ghostClass: "sortable-ghost",
    onEnd: handleRowChange,
    disabled: true,
    animation: 300,
    easing: "cubic-bezier(0.22, 1, 0.36, 1)",
  });

  const openReorderModal = (item) => {
    currentIndex.value = item.seq;
    currentIdSelect.value = item.privilege.privilege_id;
    openModal();
  };

  const openManageStockModal = (data) => {
    rewardSelect.value = data;
    let privilegeType = data.type;
    switch (privilegeType) {
      case PRIVILEGE_TYPE.REWARD:
        modalType.manageStockReward = true;
        break;
      case PRIVILEGE_TYPE.CPC:
        modalType.manageStockCouponExternal = true;
        break;
      case PRIVILEGE_TYPE.TOPUP:
        modalType.manageStockReward = true;
        break;
      case PRIVILEGE_TYPE.EXCHANGE:
        modalType.manageStockReward = true;
        break;
      default:
        break;
    }
    openModal();
  };

  const reorderInventoryManage = async (current, newIndex) => {
    fetchReorderList(currentIdSelect.value, currentIndex.value, newIndex);

    keyChange.value++;
    onCloseModalInventoryManage();
  };

  const onSelectAssignInventory = (inventory) => {
    fetchAssignEntryInventory.payload.Inventory_Id = inventory.inventory_id;
    fetchAssignEntryInventory.payload.Privilege_Id =
      privilegeSelect.value.privilege_id;
    store
      .dispatch("api/apiRequest", fetchAssignEntryInventory)
      .then((response) => { })
      .catch((error) => {
        openDefaultErrorModal(error);
      })
      .finally(() => {
        fetchPrivilegeInventory(false);
      });
  };

  const fetchPrivilegeInventory = (showLoading = true) => {
    if (showLoading) {
      loading.tableData = true;
    }
    fetchEntryMasterList.payload.Page = pagination.currentPage;
    fetchEntryMasterList.payload.Limit = pagination.perPage;
    fetchEntryMasterList.payload.Filters = filters;
    fetchEntryMasterList.payload.Ordering = ordering.value;
    store
      .dispatch("api/apiRequest", fetchEntryMasterList)
      .then((response) => {
        inventoryManageTable.payload = response.data.payload;
        pagination.rows = response.data.payload.length;
        pagination.totalRows = response.data.filtered_record;
        formStatus.expiredStatus = true

      })
      .catch((error) => {
        openDefaultErrorModal(error);
      })
      .finally(() => {
        loading.tableData = false;
        loading.tableOverlay = false;
      });
  };

  watch(
    pagination,
    () => {
      if (!loading.tableData) {
        fetchPrivilegeInventory();
      }
    },
    { deep: true }
  );

  watch(
    [filters, ordering],
    () => {
      pagination.currentPage = 1;

      fetchPrivilegeInventory();
    },
    { deep: true }
  );

  onBeforeMount(() => {
    fetchPrivilegeInventory();
    if (router.currentRoute.params.ref) {
      inventoryMaster.value = router.currentRoute.params.ref;
    }
  });

  return {
    pagination,
    rewardSelect,
    formStatus,
    fields,
    loading,
    statusOption,
    inventoryManageTable,
    inventoryManageActionList,
    sortableRowOptions,
    keyChange,
    tableModeReorder,
    currentIndex,
    modalType,
    privilegeSelect,
    onAddPrivilegeToInventory,
    resolveMappingTableAction,
    resolveStatusLabel,
    resolveStatusClass,
    reorderInventoryManage,
    onClickReorder,
    reorderInventoryManage,
    onCloseModalInventoryManage,
    resolveStockModeClass,
    resolveStockModeLabel,
    resolveDateWithSeperator,
    onPageChange,
    onFilterEntity,
    onSetValueSearch,
    onSelectFilter,
    onSelectAssignInventory,
    resolveExpiredAlertLabel,
    resolveExpiredAlertClass
  };
}
